import { useRef, useState, type FormEvent, type ReactElement } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { companyApi, ApiError } from '../lib/api';
import { useCompany } from './CompanyProvider';
import { AuthLayout, AuthField, AuthSubmit, AuthError } from './auth/AuthLayout';
import { LegalFormSelect, IndustryInput, HelpBadge } from './ui';

/**
 * Erst-Onboarding: Firma anlegen. Bewusst minimal (nur Name + Kontenrahmen
 * Pflicht), damit technikferne Nutzer in Sekunden starten. Rest später in
 * den Einstellungen.
 */
export default function Onboarding(): ReactElement {
  const navigate = useNavigate();
  // ?weitere=1 kommt vom Firmenwaehler: Dann ist die Anlage einer ZUSAETZLICHEN
  // Firma gewollt und die Weiterleitung unten darf nicht greifen.
  const [params] = useSearchParams();
  const weitereFirma = params.get('weitere') === '1';
  const { companies, loading, reload, setActive } = useCompany();
  // Schutz vor Doppel-Anlage: `busy` allein reicht nicht, weil der State-Update
  // asynchron ist und ein schneller Doppelklick zwei Requests ausloesen kann.
  const submitting = useRef(false);

  const [name, setName] = useState('');
  const [legalForm, setLegalForm] = useState('');
  const [industry, setIndustry] = useState('');
  const [chart, setChart] = useState<'SKR03' | 'SKR04'>('SKR03');
  const [kleinunternehmer, setKleinunternehmer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Server meldete eine gleichnamige Firma -> Nutzer muss aktiv bestaetigen.
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  // Mandantenlimit erreicht: nicht als blosser Fehler zeigen, sondern mit
  // Weg zum Tarif — sonst steht der Kunde vor einer Sackgasse.
  const [limitHinweis, setLimitHinweis] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting.current) return;
    setError(null);
    if (!name.trim()) {
      setError('Bitte gib den Namen deiner Firma ein.');
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      const company = await companyApi.create({
        name: name.trim(),
        legalForm: legalForm || undefined,
        industry: industry || undefined,
        chartOfAccounts: chart,
        kleinunternehmer,
        // Nur mitschicken, wenn der Nutzer die Rueckfrage schon gesehen hat.
        confirmDuplicateName: duplicateWarning !== null || undefined,
      });
      await reload();
      setActive(company.id);
      navigate('/app', { replace: true });
      return; // Ref bleibt gesperrt — die Seite wird ohnehin verlassen.
    } catch (err) {
      if (err instanceof ApiError && err.code === 'LIMIT_COMPANIES') {
        setLimitHinweis(err.message);
      } else 
      if (err instanceof ApiError && err.code === 'DUPLICATE_COMPANY_NAME') {
        setDuplicateWarning(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : 'Firma konnte nicht angelegt werden.');
      }
    } finally {
      setBusy(false);
    }
    submitting.current = false;
  }

  // Solange die Firmenliste laedt, nichts anzeigen — sonst blitzt das
  // Anlage-Formular bei Bestandskunden kurz auf.
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
      </div>
    );
  }

  // Wer bereits eine Firma hat, gehoert normalerweise nicht ins Onboarding.
  // Das passierte frueher nach einem fehlgeschlagenen Laden der Firmenliste
  // und fuehrte zu versehentlich doppelt angelegten Mandanten. Mit ?weitere=1
  // ist die Anlage dagegen ausdruecklich gewollt.
  if (companies.length > 0 && !weitereFirma) {
    return <Navigate to="/app" replace />;
  }

  return (
    <AuthLayout
      title={weitereFirma ? 'Weitere Firma anlegen' : 'Willkommen bei Saldify 👋'}
      subtitle={
        weitereFirma
          ? 'Lege einen weiteren Mandanten an – du kannst jederzeit zwischen deinen Firmen wechseln.'
          : "Lege in einem Schritt deine Firma an – los geht's."
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {limitHinweis && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
            <p className="text-sm text-amber-900 dark:text-amber-200">{limitHinweis}</p>
            <a href="/app/einstellungen/tarif" className="mt-2 inline-block text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
              Tarif ansehen →
            </a>
          </div>
        )}
        {error && <AuthError message={error} />}

        {duplicateWarning && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">Firma existiert bereits</p>
            <p className="mt-1">{duplicateWarning}</p>
            <p className="mt-2 text-amber-800">
              Wenn du zu deiner bestehenden Firma wolltest, geh zurück zur{' '}
              <button
                type="button"
                onClick={() => navigate('/app', { replace: true })}
                className="font-medium underline underline-offset-2"
              >
                Übersicht
              </button>
              . Zum Anlegen einer weiteren Firma erneut auf „Firma anlegen“ klicken.
            </p>
          </div>
        )}

        <AuthField
          label="Name deiner Firma"
          type="text"
          value={name}
          onChange={(v) => {
            setName(v);
            // Anderer Name -> alte Dubletten-Rueckfrage gilt nicht mehr.
            if (duplicateWarning) setDuplicateWarning(null);
          }}
          placeholder="z. B. Mustermann GmbH oder Max Mustermann"
          required
        />

        <div>
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Rechtsform
            <HelpBadge text="Die Rechtsform deines Unternehmens (z. B. Einzelunternehmen, GmbH). Bestimmt u. a. die Steuerpflichten. Im Zweifel: Einzelunternehmer." />
          </span>
          <LegalFormSelect value={legalForm} onChange={setLegalForm} />
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Branche / Tätigkeit
            <HelpBadge text="Deine Branche hilft der KI, Belege automatisch dem richtigen Konto zuzuordnen (z. B. Tankquittung als Fahrzeug- oder Reisekosten). Freitext – Vorschläge erscheinen beim Tippen." />
          </span>
          <IndustryInput value={industry} onChange={setIndustry} />
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Kontenrahmen
            <HelpBadge text="Standardisierter Kontenplan für die Buchhaltung. SKR03 ist bei Einzelunternehmen und kleinen Betrieben der Regelfall. Du kannst das später ändern." />
          </span>
          <div className="grid grid-cols-2 gap-3">
            {(['SKR03', 'SKR04'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setChart(opt)}
                className={`rounded-xl border px-4 py-3 text-left transition ${
                  chart === opt
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 dark:border-blue-400 dark:bg-blue-950/40 dark:ring-blue-900'
                    : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
                }`}
              >
                <div className="font-semibold text-gray-900 dark:text-gray-100">{opt}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {opt === 'SKR03' ? 'Standard (empfohlen)' : 'Bilanzgliederung'}
                </div>
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Unsicher? Lass einfach SKR03 stehen – das passt für die meisten.
          </p>
        </div>

        <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
          <input
            type="checkbox"
            checked={kleinunternehmer}
            onChange={(e) => setKleinunternehmer(e.target.checked)}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 dark:border-gray-600 dark:bg-gray-800"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Ich bin <strong>Kleinunternehmer</strong> (§19 UStG) – ich weise keine Umsatzsteuer aus.
            <HelpBadge text="Kleinunternehmer nach §19 UStG (i. d. R. unter 22.000 € Vorjahresumsatz) weisen auf Rechnungen keine Umsatzsteuer aus. Wenn aktiv, lässt Saldify die USt weg." />
          </span>
        </label>

        <AuthSubmit busy={busy}>{weitereFirma ? 'Firma anlegen' : 'Firma anlegen & loslegen'}</AuthSubmit>

        {/* Nur beim Hinzufuegen: zurueck zur laufenden Firma. Beim
            Erst-Onboarding gibt es nichts, wohin man zurueck koennte. */}
        {weitereFirma && (
          <button
            type="button"
            onClick={() => navigate('/app', { replace: true })}
            className="w-full text-center text-sm text-gray-500 hover:underline dark:text-gray-400"
          >
            Abbrechen
          </button>
        )}
      </form>
    </AuthLayout>
  );
}
